import { stateWords } from '@/ui/words';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { failureOf, said } from '@/api/errors';
import { useConfirmLink, useDecideMerge, useDraftReply, useMailboxes, useMarkComplaint, useMergeProposals, useThread } from '@/api/hooks';
import type { WireMessage } from '@/api/wire';
import { useMeOrThrow } from '@/app/MeContext';
import { Badge, Button, Kicker, Skeleton, TextAction } from '@/ui/primitives';
import { RecordHeader, statusChip, when } from '@/ui/records';
import { Refusal } from '@/ui/Refusal';
import { useToast } from '@/ui/Toast';
import { ComposedLine } from '@/ui/Composed';
import { actorLabel } from '@/today/copy';
import { ClaimControl, DraftComposer as DraftEditor } from '@/ui/drafts';
import { AgentBrief } from '@/ui/brief';
import { Explainer } from '@/ui/context';
import { useWho } from '@/ui/who';
import { onItsPage } from '@/requests/explain';
import { explainThread } from './explain';

/**
 * One conversation: its messages, and the draft waiting for a decision.
 *
 * "Nobody sends; the draft is the approval" (DECISIONS). The person edits
 * here or in Gmail, and presses Send as me or Discard. Either is recorded
 * against them (sent_unchanged / sent_edited / discarded), the platform
 * measures the edit and does the sending, and the reason the send goes as
 * them is shown beside the button rather than assumed.
 */
export function ThreadPage({ recordId }: { recordId?: string } = {}) {
  const params=useParams();
  const threadId=recordId??params.threadId??null;
  const me = useMeOrThrow();
  const navigate = useNavigate();
  const thread = useThread(threadId);
  const mailboxes = useMailboxes(me.department);
  const confirmLink = useConfirmLink();
  const toast = useToast();
  const who = useWho();

  if (thread.isPending) return <Skeleton rows={8} />;
  if (thread.isError) return <div className="p-6"><Refusal failure={failureOf(thread.error)} /></div>;

  const { thread: t, messages, drafts, subjects = [] } = thread.data;
  const pending = drafts.find((d) => d.outcome === 'pending') ?? null;
  const decided = drafts.filter((d) => d.outcome !== 'pending');
  const box = mailboxes.data?.mailboxes.find((m) => m.mailbox.address === t.mailboxAddress);
  const shared = box?.mailbox.kind === 'shared';

  return (
    <div className="mx-auto flex w-full max-w-[1040px] flex-col gap-6 p-6 phone:p-4">
      <RecordHeader
        kicker="Conversation"
        title={t.subject ?? '(no subject)'}
        subtitle={`${t.messageCount} message${t.messageCount === 1 ? '' : 's'}${t.lastFrom ? ` · last from ${t.lastFrom}` : ''}`}
        meta={t.own ? 'Your own inbox · only you see it' : t.mailboxAddress ? `On ${t.mailboxAddress}${shared ? ' · a shared box' : ''}` : null}
        chips={[
          ...(pending ? [{ label: 'DRAFTED · YOURS TO DECIDE', tone: 'warning' as const }] : []),
          ...(t.needsReply && !pending ? [{ label: 'REPLY NEEDED', tone: 'warning' as const }] : []),
          ...(statusChip(t.state) ? [statusChip(t.state)!] : []),
        ]}
        actions={
          <>
            <Button icon="back" size="row" onClick={() => void navigate('/mail')}>Back to mail</Button>
            {shared && !t.own && <ClaimControl threadId={t.id} claimedByHuman={t.claimedByHuman} me={me.humanId} />}
            {!pending && t.needsReply && !t.own && <DraftButton threadId={t.id} />}
          </>
        }
      />

      <Explainer
        label="About this conversation"
        startOpen
        context={onItsPage(
          explainThread(t, who, {
            humanId: me.humanId,
            department: me.department,
            hasDraft: pending !== null,
            sharedBox: shared,
            filedUnder: subjects.flatMap((x) => (x.name ? [x.name] : [])),
          }),
          `/mail/${t.id}`,
        )}
      />

      {t.own ? (
        <p className="m-0 rounded-brand border border-line bg-raised p-4 text-dense text-secondary" data-testid="own-thread-note">
          This is your own mail. Only you see it here — no department, and nobody else. To answer it, ask your twin in the side chat: it prepares a reply in your Gmail drafts, and only you send it.
        </p>
      ) : (
        <MergeBanner threadId={t.id} department={me.department} />
      )}

      {subjects.length > 0 && (
        <nav className="flex flex-wrap items-center gap-2 text-dense" aria-label="Filed under" data-testid="filed-under">
          <span className="text-meta">Filed under</span>
          {subjects.map((s) =>
            s.kind === 'flex' ? (
              <Badge key={`${s.kind}:${s.id}`} tone="neutral">{s.name ?? 'a record'}</Badge>
            ) : (
              <span key={`${s.kind}:${s.id}`} className="inline-flex items-center gap-1.5" data-testid="filed-subject">
                <Link to={`/${s.kind === 'company' ? 'companies' : 'contacts'}/${s.id}`} className="underline underline-offset-[3px]">
                  {s.name ?? (s.kind === 'company' ? 'A company' : 'A contact')}
                  {!s.confirmed && <span className="text-meta"> · suggested</span>}
                </Link>
                {!s.confirmed && (
                  <TextAction
                   
                    className="text-label uppercase tracking-[0.08em] text-secondary underline"
                    disabled={confirmLink.isPending}
                    onClick={() =>
                      confirmLink.mutate(s.linkId, {
                        onSuccess: () => toast({ text: 'Confirmed. It is filed there for certain now.' }),
                        onError: (e) => toast({ text: said(failureOf(e)), tone: 'danger' }),
                      })
                    }
                  >
                    Confirm
                  </TextAction>
                )}
              </span>
            ),
          )}
        </nav>
      )}

      {pending && <DraftEditor draft={pending} channel={t.primaryChannel} source={messages} />}

      <section className="flex flex-col gap-3" aria-label="Messages">
        <Kicker>The conversation</Kicker>
        <ol className="m-0 flex list-none flex-col gap-3 p-0">
          {messages.map((m) => (
            <MessageRow key={m.id} message={m} />
          ))}
        </ol>
      </section>

      {decided.length > 0 && (
        <section className="flex flex-col gap-2" aria-label="Decided drafts">
          <Kicker>Decided</Kicker>
          {decided.map((d) => (
            <div key={d.id} className="flex flex-wrap items-center gap-2 text-dense text-secondary">
              <Badge tone={statusChip(d.outcome)!.tone}>{statusChip(d.outcome)!.label}</Badge>
              <span>
                {d.decidedByName ?? 'somebody'} · {when(d.decidedAt)}
                {d.editDistance ? ` · ${d.editDistance} character${d.editDistance === 1 ? '' : 's'} changed` : ''}
              </span>
            </div>
          ))}
        </section>
      )}
      {/* Identifiers only: the subject line and the messages stay behind the scanner (§11, SG-06). */}
      <AgentBrief
        lines={[
          ['thread', t.id],
          ['department', t.department],
          ['state', t.state],
          ['needs_reply', t.needsReply],
          ['messages', messages.length],
          ['draft_pending', pending?.id],
          ...subjects.map((x): [string, string] => [x.kind, x.id]),
        ]}
      />
    </div>
  );
}

function MessageRow({ message: m }: { message: WireMessage }) {
  const agent = m.sentByAgent !== null;
  return (
    <li
      className={`flex flex-col gap-2 rounded-brand border p-4 ${m.direction === 'outbound' ? 'border-strong' : 'border-line'} ${
        m.state === 'draft' ? 'opacity-70' : ''
      }`}
      data-testid={`message-${m.direction}`}
    >
      <div className="flex flex-wrap items-baseline gap-2 text-dense">
        <span className="font-semibold text-ink">{agent ? actorLabel(m.fromAddress) : m.fromAddress}</span>
        {agent && <Badge tone="neutral">{m.composed?.geography === 'eu' ? 'AGENT · EU' : m.composed ? 'AGENT · OUTSIDE THE EU' : 'AGENT'}</Badge>}
        <span className="text-meta">{new Date(m.occurredAt).toLocaleString('en-GB')}</span>
        {m.state === 'draft' && <Badge tone="warning">DRAFT</Badge>}
        {m.scanVerdict === 'flagged' && <Badge tone="warning">FLAGGED</Badge>}
        {m.scanVerdict === 'blocked' && <Badge tone="danger">BLOCKED · NOT SHOWN TO A MODEL</Badge>}
      </div>
      {agent && <ComposedLine composed={m.composed} />}
      {m.body !== null ? (
        <p className="m-0 whitespace-pre-wrap text-body">{m.body}</p>
      ) : (
        <p className="m-0 text-dense text-meta">The body is no longer readable: its key was destroyed.</p>
      )}
      {m.direction === 'inbound' && <ComplaintControl messageId={m.id} from={m.fromAddress} />}
    </li>
  );
}

/**
 * Mark a reply as a complaint (ADR-0086). Its sender goes on the register
 * for good — nothing in the app takes an address off it — and the
 * department's complaint rate counts it. Asked twice for that reason.
 */
function ComplaintControl({ messageId, from }: { messageId: string; from: string }) {
  const mark = useMarkComplaint();
  const toast = useToast();
  const [asking, setAsking] = useState(false);
  if (mark.isSuccess) return <p className="m-0 text-dense text-secondary">Marked as a complaint. Nobody here writes to {from} again.</p>;
  return (
    <div className="flex flex-wrap items-center gap-2">
      {!asking ? (
        <Button size="row" variant="quiet" onClick={() => setAsking(true)}>
          Mark as a complaint
        </Button>
      ) : (
        <>
          <span className="text-dense text-secondary">
            {from} goes on the list of people who said stop, for good, and counts towards the complaint threshold.
          </span>
          <Button
            size="row"
            variant="danger"
            disabled={mark.isPending}
            onClick={() => mark.mutate(messageId, { onSuccess: () => toast({ text: 'Marked. Their address is on the register.' }) })}
          >
            Mark it
          </Button>
          <Button icon="close" size="row" variant="quiet" onClick={() => setAsking(false)}>Cancel</Button>
        </>
      )}
      {mark.isError && <Refusal failure={failureOf(mark.error)} compact />}
    </div>
  );
}

function DraftButton({ threadId }: { threadId: string }) {
  const draft = useDraftReply();
  const toast = useToast();
  return (
    <Button
      size="row"
      disabled={draft.isPending}
      onClick={() =>
        draft.mutate(threadId, {
          onSuccess: (r) =>
            toast({
              text:
                r.outcome === 'queued' || r.outcome === 'already_queued'
                  ? 'Asked. The draft appears here when the heartbeat has run it — a minute, with a gateway.'
                  : `Not asked: ${stateWords(r.outcome).toLowerCase()}`,
            }),
          onError: (e) => toast({ text: said(failureOf(e)), tone: 'danger' }),
        })
      }
    >
      Draft a reply
    </Button>
  );
}

/**
 * A proposal that this conversation and another are one (ADR-0031). The
 * person whose day it is on decides; anyone else in the department sees
 * that it was proposed and whose it is to decide.
 */
function MergeBanner({ threadId, department }: { threadId: string; department: string }) {
  const q = useMergeProposals(department, threadId);
  const decide = useDecideMerge();
  const toast = useToast();
  const navigate = useNavigate();
  const proposal = q.data?.proposals[0];
  if (!proposal) return null;
  const other = proposal.kept.threadId === threadId ? proposal.merged : proposal.kept;
  return (
    <section className="flex flex-col gap-2 rounded-brand border border-accent p-4" aria-label="Proposed merge" data-testid="merge-banner">
      <Kicker tone="accent">These may be one conversation</Kicker>
      <p className="m-0 text-body">
        {proposal.reason} The other is “{other.subject ?? 'no subject'}” by {other.channel}.
      </p>
      {proposal.mine ? (
        <div className="flex flex-wrap gap-2">
          <Button
            size="row"
            variant="primary"
            disabled={decide.isPending}
            onClick={() =>
              decide.mutate(
                { proposalId: proposal.id, decision: 'merge' },
                {
                  onSuccess: (r) => {
                    toast({ text: 'Merged. One conversation now, on both channels.' });
                    if (r.keptThreadId !== threadId) void navigate(`/mail/${r.keptThreadId}`);
                  },
                },
              )
            }
          >
            Merge them
          </Button>
          <Button
            size="row"
            disabled={decide.isPending}
            onClick={() =>
              decide.mutate({ proposalId: proposal.id, decision: 'keep_apart' }, { onSuccess: () => toast({ text: 'Kept apart.' }) })
            }
          >
            Keep apart
          </Button>
        </div>
      ) : (
        <p className="m-0 text-dense text-meta">It is on the day of the person who owns the contact; theirs to merge or keep apart.</p>
      )}
      {decide.isError && <Refusal failure={failureOf(decide.error)} compact />}
    </section>
  );
}
